/**
 * CAMBIAR UN PARAMETRO DE LA URL SIN TIRAR LOS DEMAS
 * ==================================================
 *
 * Esta funcion la usan los filtros, el buscador, el selector de pagina y el de elementos por hoja.
 * Devuelve la URL a la que navegar despues de cambiar UNA cosa.
 *
 * ── LO QUE HACIA ANTES, Y POR QUE SE CAMBIO ────────────────────────────────────────────────────
 *
 * Reconstruia la query DESDE CERO a partir de **una lista blanca de 21 parametros escritos a
 * mano**. Todo lo que no estuviera en esa lista desaparecia en cuanto tocabas cualquier filtro.
 *
 * Sintoma que lo destapo (01/10): con el filtro de Empresa puesto se veian 11 personas; al pasar a
 * la pagina 2 salian TODAS, sin filtro. `empresacontratante` no estaba en la lista.
 *
 * Y no era uno. Medido comparando los parametros que posee algun filtro contra la lista:
 *
 *     Personas         verificacion, empresacontratante, mostrar_dependientes, tabPersonas
 *     Publicaciones    fechaInicio, fechaFin, editor, creador      <- CUATRO de sus filtros
 *     Convocatorias    anexo
 *
 * Ademas habia una linea que ASIGNABA en vez de concatenar:
 *
 *     if (nuevoValorParam === 'pagina') {
 *       urlParams = `?pagina=${value}&registrosPorPag=${registrosPorPag}&plazas=${plazas}&activo=${activo}`;
 *     }
 *
 * Eso tiraba lo acumulado antes —`visible`— y, cuando `plazas` o `activo` no estaban, metia en la
 * URL la cadena **"null"**: `${null}` no es vacio, es el texto `null`, **que es truthy**. En la
 * vuelta siguiente `params.get('plazas')` devolvia `"null"`, pasaba el `if` y se volvia a escribir.
 * **Un valor ausente convertido en texto deja de estar ausente.**
 *
 * ── LO QUE HACE AHORA ──────────────────────────────────────────────────────────────────────────
 *
 * Parte de los parametros que YA hay y cambia solo el que toca. No hay lista que mantener, asi que
 * el proximo filtro que alguien anada funciona sin tocar este fichero — que era el fallo de fondo:
 * **una lista escrita a mano al lado de la lista que manda**.
 *
 * No es una forma nueva: es la que ya usaban `OrdenarColumnas`, `handleTabChange` y el interruptor
 * de dependientes. El raro era este.
 *
 * ── POR QUE ES SEGURO PARA LAS 11 PANTALLAS QUE LA USAN ────────────────────────────────────────
 *
 * Porque es MAS PERMISIVO: antes se tiraba lo desconocido, ahora se conserva. Ninguna pantalla
 * puede depender de que un parametro desaparezca al tocar otro — se busco y no hay ninguna. Los
 * dos unicos descartes que SI eran intencionados siguen estando:
 *
 *   · cambiar un parametro lo sustituye (`set`, no se duplica)
 *   · dejarlo en blanco lo BORRA, que es como se limpia un filtro
 *
 * Lo que si cambia de forma: los valores van **codificados** (`URLSearchParams.toString()`). Antes
 * se concatenaban en crudo, asi que una busqueda con `&` partia la URL. Se lee con `params.get()`,
 * que decodifica, de modo que ninguna pantalla ve diferencia — salvo que la URL ahora aguanta
 * textos con simbolos.
 *
 * ── LO QUE NO SE TOCA, A PROPOSITO ─────────────────────────────────────────────────────────────
 *
 * Cambiar un filtro NO devuelve a la pagina 1. Es discutible —`OrdenarColumnas` si lo hace— pero
 * es el comportamiento de hoy y cambiarlo no es el fallo que se vino a arreglar. Queda dicho aqui
 * para que la proxima persona sepa que es una decision y no un olvido.
 */

/** Un `${null}` viejo dejo cadenas "null" en URLs guardadas; se limpian al pasar por aqui. */
const esBasura = (v) => v === 'null' || v === 'undefined';

/** Aplica un cambio sobre unos `params`. Un valor vacio BORRA, que es como se limpia un filtro. */
const aplicarUno = (params, clave, value) => {
    if (value === '' || value === null || value === undefined) params.delete(clave);
    else params.set(clave, value);
};

/** Quita las cadenas "null"/"undefined" que dejo un `${null}` viejo en URLs guardadas. */
const limpiar = (params) => {
    for (const [k, v] of Array.from(params.entries())) {
        if (esBasura(v)) params.delete(k);
    }
    return params;
};

const construir = (params) => {
    const query = limpiar(params).toString();
    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
};

/**
 * VARIOS PARAMETROS A LA VEZ, que `CambioDeURL` no puede hacer.
 *
 * `CambioDeURL` lee `window.location` en CADA llamada, asi que encadenar dos no acumula: la
 * segunda parte de la URL de antes y pisa lo que hizo la primera. Y hace falta cambiar dos a la
 * vez constantemente, porque **todo cambio de filtro tiene que resetear la pagina**: quedarse en
 * la 3 al filtrar deja una tabla vacia que parece «no hay nada» y es «no hay pagina 3 con esto».
 *
 * Se anade AQUI y no en la pantalla que lo necesita: es la misma pregunta que ya contesta este
 * fichero, y escribirla al lado seria la segunda copia.
 *
 * @param {Record<string, string|number|null>} cambios  vacio o null BORRA ese parametro
 */
export const CambiosDeURL = (cambios) => {
    const params = new URLSearchParams(window.location.search);
    for (const [clave, valor] of Object.entries(cambios || {})) aplicarUno(params, clave, valor);
    return construir(params);
};

const CambioDeURL = (nuevoValorParam) => {
    return (value) => {
        const params = new URLSearchParams(window.location.search);

        // Herencia: se llegaba a aceptar un objeto {ordenarColumna, orden}. Hoy no lo usa nadie
        // —`OrdenarColumnas` construye su URL por su cuenta— pero se mantiene para no romper a
        // quien lo hiciera sin que este fichero se entere.
        if (nuevoValorParam && typeof nuevoValorParam === 'object') {
            if (nuevoValorParam.ordenarColumna !== undefined) {
                params.set('ordenarColumna', nuevoValorParam.ordenarColumna);
            }
            if (nuevoValorParam.orden !== undefined) {
                params.set('orden', nuevoValorParam.orden);
            }
        } else if (typeof nuevoValorParam === 'string' && nuevoValorParam !== '') {
            // Un valor vacio BORRA el parametro: asi se limpia un filtro, y es el unico descarte
            // que esta funcion hace a proposito.
            aplicarUno(params, nuevoValorParam, value);
        }

        return construir(params);
    };
};

export default CambioDeURL;
